import { useCallback, useContext, HTMLAttributes } from "react";
import { Dropdown, Nav, Navbar, NavDropdown } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ProfileImage from "../profile-image";
import { FormattedMessage, useIntl } from "react-intl";
import "./app-header.scss";
import { Organization } from "../../model/Organization";
import logo from "../logo";
import { getClientId, getUserUIBase } from "../../api/ApiImplementation";
import { LinkContainer } from "react-router-bootstrap";
import UIConfiguration from "../../ui-configuration/configuration-provider";
import TooltipTrigger from "../tooltip-trigger";
import LanguageContext from "../../language-context";
import { UserValues } from "../../localization/user";
import { asDukeLocale, asIntlLocale } from "../localize/localize-view";
import { getAppBase } from "../../util/env";
import { ResolvedFrom } from "../../utils/locale";

//<editor-fold desc="Props">
export interface AppHeaderUser {
  id: string;
  name?: string;
  email?: string;
}
export interface AppHeaderDOMProps
  extends Omit<HTMLAttributes<HTMLDivElement>, "children"> {
  disabled?: boolean;
}

export interface AppHeaderProps extends AppHeaderDOMProps {
  user?: AppHeaderUser;
  selectedOrganization?: Organization;
  organizations?: Organization[];
  onSelectOrganization?: (orgId: string) => void;
  disableOrganizationSelect?: boolean;
}
//</editor-fold>

function AppHeader(props: AppHeaderProps) {
  //<editor-fold desc="Local variables">
  const {
    user,
    selectedOrganization,
    onSelectOrganization,
    organizations,
    disabled,
    disableOrganizationSelect,
    ...other
  } = props;
  const intl = useIntl();
  /* eslint-disable no-template-curly-in-string */
  /* tslint:disable:no-template-curly-in-string */
  const idp_base = getUserUIBase().replace(
    "${locale}",
    intl.locale !== intl.defaultLocale ? "/" + asDukeLocale(intl.locale) : ""
  );
  /* tslint:enable:no-template-curly-in-string */
  /* eslint-enable no-template-curly-in-string */
  const organizationDropdownItemClickHandler = useCallback(
    (eventKey: any) => {
      if (onSelectOrganization) {
        onSelectOrganization(eventKey);
      }
    },
    [onSelectOrganization]
  );

  const { conf } = useContext(UIConfiguration);
  // could and possible should be wrapped in a memo, but unfortunately the key?.key2? syntax does not work with keys
  // that need to be wrapped in ["key3"], so resolving the dependencies would likely consume more than the memo can
  // save.
  const localeConf =
    conf.functionality && conf.functionality.localization
      ? conf.functionality.localization
      : {};
  //</editor-fold>

  //<editor-fold desc="Partials">

  const langDropdown =
    localeConf["show-switcher"] &&
    localeConf.locales &&
    localeConf.locales.length > 0 ? (
      <LanguageContext.Consumer>
        {(context) => {
          let currentLocale = context.locale;
          const languageNames = new Intl.DisplayNames(
            [asIntlLocale(currentLocale?.value as string)],
            { type: "language" }
          );
          return (
            <Nav className={"ml-2 language-nav"}>
              <NavDropdown
                data-test-select-language-tool
                title={
                  <>
                    <span data-test-selected-language>
                      {currentLocale && currentLocale.label}
                      {(!currentLocale || !currentLocale.label) &&
                        languageNames.of(intl.locale)}
                    </span>
                  </>
                }
                id="header-nav-language-dropdown"
                onSelect={context.changeLanguage}
              >
                <Dropdown.Header className={"d-flex"}>
                  <FormattedMessage
                    defaultMessage="Select language"
                    description="Change language dropdown menu heading"
                  />
                </Dropdown.Header>
                {localeConf["show-switcher"] &&
                  localeConf.locales &&
                  localeConf.locales.length > 0 &&
                  localeConf.locales.map((l) => {
                    const active =
                      l.value === (context.locale.value || intl.locale);
                    return (
                      <TooltipTrigger
                        tipKey={"langaugeSelectTip_" + l.value}
                        key={l.value}
                        tip={
                          context.resolvedFrom === ResolvedFrom.store &&
                          active ? (
                            <FormattedMessage
                              defaultMessage="Stored user preference"
                              description="active dropdown language tooltip for a language that is stored as user preference"
                            />
                          ) : context.resolvedFrom === ResolvedFrom.detected &&
                            active ? (
                            <FormattedMessage
                              defaultMessage="Detected browser setting"
                              description="active dropdown language tooltip for a language that detected automatically"
                            />
                          ) : undefined
                        }
                      >
                        <NavDropdown.Item
                          key={l.value}
                          data-test-select-language={l.value}
                          active={active}
                          eventKey={l.value}
                          className={
                            "align-items-center d-flex flex-row justify-content-between dropdown-item"
                          }
                        >
                          <span>{l.label}</span>
                          {active &&
                            context.resolvedFrom === ResolvedFrom.store && (
                              <FontAwesomeIcon
                                icon={"user-check"}
                                fixedWidth={true}
                                className={"ml-4"}
                              ></FontAwesomeIcon>
                            )}
                          {active &&
                            context.resolvedFrom === ResolvedFrom.detected && (
                              <FontAwesomeIcon
                                icon={"globe"}
                                fixedWidth={true}
                                className={"ml-4"}
                              ></FontAwesomeIcon>
                            )}
                        </NavDropdown.Item>
                      </TooltipTrigger>
                    );
                  })}
              </NavDropdown>
            </Nav>
          );
        }}
      </LanguageContext.Consumer>
    ) : undefined;
  const orgDropdown =
    organizations && organizations.length > 1 ? (
      <Nav className={"ml-2 organization-nav"}>
        <TooltipTrigger
          tipKey={"organizationSelectDisabledTip"}
          tip={
            disableOrganizationSelect ? (
              <FormattedMessage
                defaultMessage="Switching organization is disabled until all pending actions are completed."
                description="tooltip to be shown when the change organization tool is disabled due to ongoing api call"
              />
            ) : undefined
          }
        >
          <NavDropdown
            disabled={disableOrganizationSelect}
            onSelect={organizationDropdownItemClickHandler}
            title={
              <span data-test-select-organization-tool>
                {selectedOrganization ? (
                  selectedOrganization.name
                ) : (
                  <FormattedMessage
                    defaultMessage="Select organization"
                    description="Select organization dropdown default option label"
                  />
                )}
              </span>
            }
            id="header-nav-organization-dropdown"
          >
            <Dropdown.Header className={"d-flex"}>
              <FormattedMessage
                defaultMessage="Select active organization"
                description="Select organization dropdown menu heading"
              />
            </Dropdown.Header>
            {organizations &&
              organizations.length &&
              organizations.map((itm) => {
                return (
                  <NavDropdown.Item
                    key={itm.id}
                    data-test-select-organization={itm.id}
                    eventKey={itm.id}
                    active={
                      selectedOrganization && selectedOrganization.id === itm.id
                    }
                  >
                    <span>{itm.name}</span>
                  </NavDropdown.Item>
                );
              })}
          </NavDropdown>
        </TooltipTrigger>
      </Nav>
    ) : null;

  const userDropdown = (
    <>
      {user && (
        <NavDropdown
          data-test-user-menu={user.id as string}
          title={
            <>
              <ProfileImage className="mr-2" />
              <span className="navbar-toggler-icon"></span>
            </>
          }
          id="header-nav-profile-dropdown"
        >
          <Dropdown.Header className={"d-flex"}>
            <ProfileImage className="mr-2" />
            <span>
              {user.name
                ? user.name
                : intl.formatMessage(UserValues.displayName.undefined)}
              {user.email && (
                <>
                  <br />
                  <em>{user.email}</em>
                </>
              )}
            </span>
          </Dropdown.Header>
          <NavDropdown.Item href={idp_base} target={"_blank"} active={false}>
            <FontAwesomeIcon icon="user-cog" className="icon mr-2" />
            <FormattedMessage
              defaultMessage="My Profile"
              description={"Profile menu item label for the profile page"}
            />
          </NavDropdown.Item>
          <NavDropdown.Divider />
          <NavDropdown.Item
            href={
              idp_base +
              "/oauth20/signout?client_id=" +
              encodeURIComponent(getClientId()) +
              "&RelayState=" +
              encodeURIComponent(
                "url=" +
                  window.location.href.split(getAppBase())[0] +
                  getAppBase() +
                  "/logout"
              )
            }
          >
            <FontAwesomeIcon icon="power-off" className="icon mr-2" />
            <FormattedMessage
              defaultMessage="Logout"
              description={"Profile menu item label for logging out"}
            />
          </NavDropdown.Item>
        </NavDropdown>
      )}
      {!user && (
        <Navbar.Text>
          <FormattedMessage
            defaultMessage="Guest"
            description="Label to be shown in place of Profile menu when there is no session"
          />
        </Navbar.Text>
      )}
    </>
  );
  //</editor-fold>

  return (
    <header
      data-test-header
      id="header"
      {...other}
      className={disabled ? "disabled" : ""}
    >
      <Navbar fixed={"top"}>
        <LinkContainer to="/">
          <Navbar.Brand data-test-logo>
            <img src={logo} alt={""} className={"mr-3"} />
            <FormattedMessage
              defaultMessage="Orgadmin"
              description="Name of this admin tool in the header, displayed next to logo"
            />
          </Navbar.Brand>
        </LinkContainer>
        <div
          className={
            "ml-auto d-inline-flex justify-content-end align-items-center"
          }
        >
          {orgDropdown}
          {langDropdown}
          <Nav className={"ml-2 user-nav"}>{userDropdown}</Nav>
        </div>
      </Navbar>
    </header>
  );
}

export default AppHeader;
